/*
 * The schedule engine: which days a job runs (BUILD-SPEC 4.1, copied from the WordPress Steward's steward_due_on).
 * Dates are "YYYY-MM-DD" in the company's time zone; the math is on calendar days only, so no time zone can shift a day.
 *
 * - one-time: only on the start date.
 * - daily: every day from the start date.
 * - weekly: every week on the start date's weekday.
 * - biweekly: every 14 days from the start date.
 * - monthly: the start date's weekday in the same week of the month (days 1-7 are the 1st week, 8-14 the 2nd, 15-21 the
 *   3rd, 22-28 the 4th). A job that started in a 5th week runs in the last week of each month.
 * - bimonthly: the monthly rule, every other month counted from the start date's month.
 * Nothing before the start date, after the end date or on a skipped date. Moved visits (draft 2) replace these by their
 * original date. A frequency this does not know runs on no day, rather than guessing.
 *
 * Repeat day (owner, Oct 30: "start date shouldnt dictate the recurring date, the recurring schedule should"): a job with
 * a repeat day (1 Monday to 7 Sunday; a route job takes its route's day) runs weekly, every 2 weeks and monthly on that
 * day, counted from the first such day on or after the start date. The start date only says when it begins.
 *
 * Custom (owner, Oct 31, like Jobber's Set up recurring schedule): every N days, weeks (on picked weekdays), months (on
 * picked dates, or the 1st to 4th or last weekday) or years. As needed runs on no day: the office books each visit.
 * Repeat from: when the repeat changes from a visit on, the new repeat counts from that day. The visits before it were
 * given rows first, so they stay where they were.
 */

export const FREQUENCIES = ["one-time", "daily", "weekly", "biweekly", "monthly", "bimonthly", "custom", "as-needed"] as const;
export type Frequency = (typeof FREQUENCIES)[number];

export interface ScheduledJob {
  frequency: string;
  startDate: string | null;
  endsOn?: string | null;
  skipDates?: readonly string[];
  /** 1 Monday to 7 Sunday: the day it repeats on. Null: the start date's weekday. */
  repeatDay?: number | null;
  /** The custom repeat, when frequency is custom. */
  repeatRule?: RepeatRule | null;
  /** The repeat counts from this day when it is after the start date (the repeat was changed from a visit on). */
  repeatFrom?: string | null;
}

export const REPEAT_UNITS = ["day", "week", "month", "year"] as const;
export type RepeatUnit = (typeof REPEAT_UNITS)[number];
/** A month date that means the last day of the month. */
export const LAST_DAY = 32;
/** A week of the month that means the last one. */
export const LAST_WEEK = 5;

/** A custom repeat: every N days, weeks, months or years. */
export interface RepeatRule {
  every: number;
  unit: RepeatUnit;
  /** week: the days it runs, 1 Monday to 7 Sunday. Empty: the first visit's weekday. */
  weekdays?: number[] | undefined;
  /** month by date: 1 to 31, or LAST_DAY. A date the month does not have runs on its last day. Empty: the first visit's. */
  monthDays?: number[] | undefined;
  /** month by weekday: the 1st to 4th (or LAST_WEEK) Monday to Sunday. Wins over monthDays when it has any. */
  monthWeekdays?: { nth: number; day: number }[] | undefined;
}

const DAY = 86_400_000;
const parse = (d: string): number => Date.UTC(Number(d.slice(0, 4)), Number(d.slice(5, 7)) - 1, Number(d.slice(8, 10)));
const fmt = (t: number): string => new Date(t).toISOString().slice(0, 10);

/** The date n days after (or before) a date. */
export function addDays(date: string, n: number): string {
  return fmt(parse(date) + n * DAY);
}

/** Whole days from a to b (negative when b is before a). */
export function daysBetween(a: string, b: string): number {
  return Math.round((parse(b) - parse(a)) / DAY);
}

/** 0 Sunday to 6 Saturday. */
export function weekday(date: string): number {
  return new Date(parse(date)).getUTCDay();
}

function daysInMonth(date: string): number {
  return new Date(Date.UTC(Number(date.slice(0, 4)), Number(date.slice(5, 7)), 0)).getUTCDate();
}

/** Same weekday and same week of the month as the start, with a 5th-week start running in the last week. */
function monthlyDue(start: string, date: string): boolean {
  if (weekday(start) !== weekday(date)) return false;
  const want = Math.ceil(Number(start.slice(8, 10)) / 7);
  const day = Number(date.slice(8, 10));
  const nth = Math.ceil(day / 7);
  if (nth === want) return true;
  return want === 5 && nth === 4 && day + 7 > daysInMonth(date);
}

const monthIndex = (d: string) => Number(d.slice(0, 4)) * 12 + Number(d.slice(5, 7));

/** The first day on or after date that falls on a repeat day (1 Monday to 7 Sunday). */
export function onOrAfter(date: string, repeatDay: number): string {
  return addDays(date, ((repeatDay % 7) - weekday(date) + 7) % 7);
}

/** 1 Monday to 7 Sunday. */
export function isoDay(date: string): number {
  return weekday(date) || 7;
}

const REPEATS_ON_A_DAY = new Set(["weekly", "biweekly", "monthly", "bimonthly"]);

type Anchored = Pick<ScheduledJob, "frequency" | "startDate" | "repeatDay" | "repeatFrom" | "repeatRule">;

/** The day the repeat counts from: the start date (or a later repeat from), moved on to the repeat day when it has one. */
function anchor(job: Anchored): string | null {
  const from = job.repeatFrom && (!job.startDate || job.repeatFrom > job.startDate) ? job.repeatFrom : job.startDate;
  if (!from || !job.repeatDay || !REPEATS_ON_A_DAY.has(job.frequency)) return from;
  return onOrAfter(from, job.repeatDay);
}

/** The date the repeats count from: the start date, or the first repeat day on or after it (custom: its first visit). */
export function firstRepeat(job: Anchored): string | null {
  const start = anchor(job);
  if (!start || job.frequency !== "custom") return start;
  for (let d = start, i = 0; i < 800; d = addDays(d, 1), i++) if (customDue(job.repeatRule, start, d)) return d;
  return null;
}

/** The same date of the month, or the month's last day when the month is shorter. */
function sameDate(want: number, date: string): boolean {
  const last = daysInMonth(date);
  return Number(date.slice(8, 10)) === (want >= LAST_DAY ? last : Math.min(want, last));
}

/** Does a custom repeat that counts from start run on date? */
function customDue(rule: RepeatRule | null | undefined, start: string, date: string): boolean {
  if (!rule) return false;
  const every = Math.max(1, Math.floor(rule.every) || 1);
  switch (rule.unit) {
    case "day":
      return daysBetween(start, date) % every === 0;
    case "week": {
      const days = rule.weekdays?.length ? rule.weekdays : [isoDay(start)];
      return days.includes(isoDay(date)) && (daysBetween(mondayOf(start), mondayOf(date)) / 7) % every === 0;
    }
    case "month": {
      if ((monthIndex(date) - monthIndex(start)) % every !== 0) return false;
      if (rule.monthWeekdays?.length) {
        const day = Number(date.slice(8, 10));
        return rule.monthWeekdays.some(
          (m) => m.day === isoDay(date) && (m.nth === Math.ceil(day / 7) || (m.nth >= LAST_WEEK && day + 7 > daysInMonth(date))),
        );
      }
      const days = rule.monthDays?.length ? rule.monthDays : [Number(start.slice(8, 10))];
      return days.some((d) => sameDate(d, date));
    }
    case "year":
      return (
        (Number(date.slice(0, 4)) - Number(start.slice(0, 4))) % every === 0 &&
        date.slice(5, 7) === start.slice(5, 7) &&
        sameDate(Number(start.slice(8, 10)), date)
      );
    default:
      return false;
  }
}

/** Does the job run on this date? */
export function isDue(job: ScheduledJob, date: string): boolean {
  const start = anchor(job);
  if (!start || date < start) return false;
  if (job.endsOn && date > job.endsOn) return false;
  if (job.skipDates?.includes(date)) return false;
  const days = daysBetween(start, date);
  switch (job.frequency) {
    case "one-time":
      return days === 0;
    case "daily":
      return true;
    case "weekly":
      return days % 7 === 0;
    case "biweekly":
      return days % 14 === 0;
    case "monthly":
      return monthlyDue(start, date);
    case "bimonthly":
      return monthlyDue(start, date) && (monthIndex(date) - monthIndex(start)) % 2 === 0;
    case "custom":
      return customDue(job.repeatRule, start, date);
    default:
      return false;
  }
}

/** Every date the job runs from `from` to `to`, both included, in order. */
export function occurrences(job: ScheduledJob, from: string, to: string): string[] {
  const out: string[] = [];
  if (!job.startDate) return out;
  for (let d = from < job.startDate ? job.startDate : from; d <= to; d = addDays(d, 1)) if (isDue(job, d)) out.push(d);
  return out;
}

/** Monday of the week a date is in (payroll and reports run Monday to Sunday). */
export function mondayOf(date: string): string {
  return addDays(date, -((weekday(date) + 6) % 7));
}
